import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ScheduledActivity, UpdateActivityStatusRequest } from '../../interfaces/activity-schedule.interface';
import { ActivityScheduleService } from '../../services/activity-schedule.service';
import { ActivityBookingComponent } from '../activity-booking/activity-booking.component';
import { NotificationService } from 'src/app/services/notification.service';
import { helper } from 'src/environments/helper';

@Component({
    selector: 'app-cerere-activity-widget',
    templateUrl: './cerere-activity-widget.component.html',
    styleUrls: ['./cerere-activity-widget.component.scss']
})
export class CerereActivityWidgetComponent implements OnInit, OnDestroy, OnChanges {
    @Input() cerereId!: number;

    private destroy$ = new Subject<void>();

    activities: ScheduledActivity[] = [];
    isLoading = false;
    showAll = false;

    helper = helper;

    statusOptions = [
        { key: 'scheduled', label: 'Programată', color: '#2196F3' },
        { key: 'completed', label: 'Finalizată', color: '#4CAF50' },
        { key: 'cancelled', label: 'Anulată', color: '#F44336' },
        { key: 'rescheduled', label: 'Reprogramată', color: '#FF9800' },
        { key: 'no_show', label: 'Absent', color: '#9E9E9E' }
    ];

    constructor(
        private activityScheduleService: ActivityScheduleService,
        private notificationService: NotificationService,
        private dialog: MatDialog
    ) { }

    ngOnInit(): void {
        this.loadCerereActivities();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['cerereId'] && !changes['cerereId'].firstChange) {
            this.loadCerereActivities();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadCerereActivities(): void {
        if (!this.cerereId || this.cerereId === 0) {
            console.log('CerereActivityWidget: No valid cerereId, skipping load. Current cerereId:', this.cerereId);
            return;
        }

        console.log('CerereActivityWidget: Loading activities for cerereId:', this.cerereId);
        this.isLoading = true;
        this.activityScheduleService.getCerereActivities(this.cerereId).pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: (activities) => {
                console.log('CerereActivityWidget: Loaded activities:', activities);
                this.activities = activities;
                this.isLoading = false;
            },
            error: (error) => {
                console.error('Error loading cerere activities:', error);
                this.notificationService.warningSwal(
                    'Eroare',
                    'Nu s-au putut încărca activitățile pentru această cerere',
                    'error'
                );
                this.isLoading = false;
            }
        });
    }

    scheduleNewActivity(): void {
        const dialogRef = this.dialog.open(ActivityBookingComponent, {
            width: '800px',
            maxHeight: '90vh',
            disableClose: true,
            data: {
                cerereId: this.cerereId,
                mode: 'create'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.notificationService.successSwal('Succes', 'Activitatea a fost programată cu succes!');
                this.loadCerereActivities(); // Refresh the list
            }
        });
    }

    openActivityDetails(activity: ScheduledActivity): void {
        const dialogRef = this.dialog.open(ActivityBookingComponent, {
            width: '800px',
            maxHeight: '90vh',
            disableClose: true,
            autoFocus: false,
            data: {
                mode: 'edit',
                activityId: activity.id,
                prefilledData: activity,
                edit_activity: activity
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.notificationService.successSwal('Succes', 'Activitatea a fost actualizată cu succes!');
                this.loadCerereActivities(); // Refresh the list
            }
        });
    }

    getStatusLabel(status: string): string {
        const option = this.statusOptions.find(opt => opt.key === status);
        return option?.label || status;
    }

    getStatusColor(status: string): string {
        const option = this.statusOptions.find(opt => opt.key === status);
        return option?.color || '#6c757d';
    }

    formatDateTime(date: string, time: string): string {
        if (!date || !time) return 'N/A';
        try {
            const dateObj = new Date(`${date}T${time}`);
            return dateObj.toLocaleDateString('ro-RO', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return `${date} ${time}`;
        }
    }

    toggleShowAll(): void {
        this.showAll = !this.showAll;
    }

    get displayedActivities(): ScheduledActivity[] {
        return this.showAll ? this.activities : this.activities.slice(0, 3);
    }

    get hasMoreActivities(): boolean {
        return this.activities.length > 3;
    }

    markActivityCompleted(activity: ScheduledActivity, event: Event): void {
        event.stopPropagation();

        const userId = 1; // Should get from AuthService in real implementation
        const statusUpdate: UpdateActivityStatusRequest = {
            status: 'completed',
            user_id: userId,
            notes: 'Marcat ca finalizat din lista cererii'
        };

        this.activityScheduleService.updateActivityStatus(activity.id, statusUpdate).pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: () => {
                this.notificationService.successSwal('Succes', 'Activitatea a fost marcată ca finalizată');
                this.loadCerereActivities();
            },
            error: (error) => {
                console.error('Error updating activity status:', error);
                this.notificationService.warningSwal('Eroare', 'Nu s-a putut actualiza statusul activității');
            }
        });
    }

    cancelActivity(activity: ScheduledActivity, event: Event): void {
        event.stopPropagation();

        this.notificationService.confirmSwal(
            'Confirmare',
            'Sigur doriți să anulați această activitate?',
            'Anulează activitatea'
        ).then((result) => {
            if (result.isConfirmed) {
                const userId = 1; // Should get from AuthService in real implementation
                const statusUpdate: UpdateActivityStatusRequest = {
                    status: 'cancelled',
                    user_id: userId,
                    notes: 'Anulat din lista cererii'
                };

                this.activityScheduleService.updateActivityStatus(activity.id, statusUpdate).pipe(
                    takeUntil(this.destroy$)
                ).subscribe({
                    next: () => {
                        this.notificationService.successSwal('Succes', 'Activitatea a fost anulată');
                        this.loadCerereActivities();
                    },
                    error: (error) => {
                        console.error('Error cancelling activity:', error);
                        this.notificationService.warningSwal('Eroare', 'Nu s-a putut anula activitatea');
                    }
                });
            }
        });
    }
}